import * as moment from 'moment';
import { Observable } from 'rxjs';
import { ChartDataSets } from 'chart.js';
import { map, take } from 'rxjs/operators';
import { Component, Input } from '@angular/core';
import { ChartService, DataEnergyService, DataStateService, DataWorkService, FeatureToggleService } from 'app/services';
import {
  IDateRange,
  IChartFilter,
  IMachineTotals,
  IMachineFromApi,
  GraphPeriodType,
  IMachineTotalJobs,
  IMachineJobsFilter,
} from 'app/models';
import { MatRadioChange } from '@angular/material/radio';
import { MatDialog } from '@angular/material/dialog';
import { Constants } from '@config/constants';
import { MessageService } from 'app/services/message/message.service';
import { saveAs } from 'file-saver';
import { AlertDialogComponent, AlertDialogModel } from '../alert-dialog/alert-dialog.component';
@Component({
  selector: 'app-machines-component',
  templateUrl: './machines.component.html',
  styleUrls: ['./machines.component.scss'],
})
export class MachinesComponent {
  @Input()
  listOfMachinesFromApi: Array<IMachineFromApi> = [];

  machineOverallConsumption: number | undefined = undefined;

  machinePowerAlgorithm: string | undefined = undefined;

  machineWaitRatio = 0;

  machineErrorRatio = 0;

  machineRunningRatio = 0;

  sumOfJobsChartLabels: string[] = [];

  totalCutCreaseChartLabels: string[] = [];

  dateRange: IDateRange | null = null;

  zoneId?: string;

  selectedPeriod = GraphPeriodType.DAILY;

  sumOfJobsChartDataSets: Array<ChartDataSets> = [];

  totalCutCreaseChartDataSets: Array<ChartDataSets> = [];

  selectedMachine: IMachineFromApi | null = null;

  machineTotalJobsList: Array<IMachineTotalJobs> = [];

  selectedMachineTotals$: Observable<IMachineTotals> | null = null;

  constructor(
    private readonly chartService: ChartService,
    private readonly dataWorkService: DataWorkService,
    private readonly dataEnergyService: DataEnergyService,
    private readonly dataStateService: DataStateService,
    private readonly constants: Constants,
    private readonly messageService: MessageService,
    private featureToggleService: FeatureToggleService,
    private dialog: MatDialog,
  ) { }

  onDataWorkCsvDownloadClicked() {
    if (this.selectedMachine && this.dateRange) {
      const queryParams = `machineId=${this.selectedMachine.id
        }&from=${moment(this.dateRange.startDate).format('YYYY-MM-DDTHH:mm:ss')
        }&to=${moment(this.dateRange.endDate).format('YYYY-MM-DDTHH:mm:ss')
        }`;
      this.dataWorkService.downloadDataWorkCSV(queryParams).subscribe((fileBlob: Blob) => {
        saveAs(fileBlob, `${this.selectedMachine?.id}-data-work.csv`);
      });
    }
  }

  onDataEnergyCsvDownloadClicked() {
    if (this.selectedMachine && this.dateRange) {
      const daysDifference = this.dateRange.endDate.diff(this.dateRange.startDate, 'days');

      if (daysDifference >= 7) {
        const dialogData = new AlertDialogModel(
          'Intervallo troppo lungo',
          'L\'intervallo selezionato è superiore a 7 giorni. Ridurre il periodo per procedere con il download.'
        );

        this.dialog.open(AlertDialogComponent, {
          maxWidth: '400px',
          data: dialogData
        });
        return;
      }

      const queryParams = `customerId=${this.selectedMachine.customerId
        }&machineId=${this.selectedMachine.id
        }&from=${moment(this.dateRange.startDate).format('YYYY-MM-DDTHH:mm:ss')
        }&to=${moment(this.dateRange.endDate).format('YYYY-MM-DDTHH:mm:ss')
        }`;
      this.dataEnergyService.downloadDataEnergyCSV(queryParams).subscribe((fileBlob: Blob) => {
        saveAs(fileBlob, `${this.selectedMachine?.id}-data-energy.csv`);
      });
    }
  }

  shouldShowDataEnergyButton(): boolean {
    return this.selectedMachine?.actualMeasurementAlgorithm === 'POLLING_DETECTION';
  }

  handleSelectedMachineChange(selectedMachineId?: number): void {
    if (selectedMachineId != null) {
      this.selectedMachine =
        this.listOfMachinesFromApi.find(
          (machine) => machine.id === selectedMachineId
        ) || null;

      if (this.selectedMachine && this.selectedMachine.id) {
        this.updateMachineTotalJobsList({
          machineId: this.selectedMachine.id,
        });

        this.getSelectedMachineTotals({
          machineId: this.selectedMachine?.id,
        });
      }
    } else {
      this.selectedMachine = null;
    }
  }

  handleFilterByDateChanged(dateRange: IDateRange): void {
    this.dateRange = dateRange;
    this.zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (this.selectedMachine && this.selectedMachine.id) {
      this.updateMachineTotalJobsList({
        machineId: this.selectedMachine.id,
        dateRange,
      });

      this.getSelectedMachineTotals({
        machineId: this.selectedMachine.id,
        dateRange,
      });

      this.getSumOfJobsChartData({
        machineId: this.selectedMachine.id,
        dateRange,
        graphPeriodType: this.selectedPeriod,
        zoneId: this.zoneId
      });

      this.getTotalCutCreaseChartData({
        machineId: this.selectedMachine.id,
        dateRange,
        graphPeriodType: this.selectedPeriod,
        zoneId: this.zoneId
      });

      this.getMachineRunningRatio({
        machineId: this.selectedMachine.id,
        dateRange,
        zoneId: this.zoneId
      });

      this.getMachineErrorRatio({
        machineId: this.selectedMachine.id,
        dateRange,
        zoneId: this.zoneId
      });

      this.getMachineWaitRatio({
        machineId: this.selectedMachine.id,
        dateRange,
        zoneId: this.zoneId
      });

      this.getMachinOverallConsumption({
        customerId: this.selectedMachine.customerId,
        machineId: this.selectedMachine.id,
        dateRange,
        zoneId: this.zoneId
      });
    }
  }

  handleChartPeriodChanged({ value }: MatRadioChange) {
    this.selectedPeriod = value;

    if (this.selectedMachine && this.dateRange) {
      this.getSumOfJobsChartData({
        machineId: this.selectedMachine.id,
        dateRange: this.dateRange,
        graphPeriodType: this.selectedPeriod,
        zoneId: this.zoneId
      });

      this.getTotalCutCreaseChartData({
        machineId: this.selectedMachine.id,
        dateRange: this.dateRange,
        graphPeriodType: this.selectedPeriod,
        zoneId: this.zoneId
      });
    }
  }

  public isIntervalInThreshold(dateRange: IDateRange | null): boolean {
    if (!dateRange) {
      return false;
    }
    const result = dateRange.endDate.diff(dateRange.startDate, 'days') < this.constants.INTERVAL_THRESHOLD_DAYS_TO_SHOW_JOB_DETAILS;
    return result;
  }

  private updateMachineTotalJobsList({
    machineId: id,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
  }: IMachineJobsFilter): void {
    if (!this.isIntervalInThreshold({ startDate, endDate })) {
      this.machineTotalJobsList = [];
      this.messageService.showDirectSimpleMessage(` If Job details exists they are not displayed due to too long a date interval `);
      return;
    }
    this.dataWorkService
      .getTotalJobsByMachine({
        machineId: id,
        dateRange: { startDate, endDate },
      })
      .pipe(
        take(1),
        map((res) =>
          res.map(
            ({
              fileName,
              machineId,
              totalTime,
              dateTimeWork,
              cutMetersAsString,
              totalTimeAsHHmmss,
              creaseMetersAsString,
              physicalIncoherent,
            }) => ({
              fileName,
              machineId,
              totalTime,
              dateTimeWork,
              cutMetersAsString,
              totalTimeAsHHmmss,
              creaseMetersAsString,
              average: parseFloat(
                (
                  (parseFloat(cutMetersAsString) +
                    parseFloat(creaseMetersAsString)) /
                  (totalTime / 3600)
                ).toString()
              ).toFixed(2),
              physicalIncoherent,
            })
          )
        )
      )
      .subscribe(
        (data) => (this.machineTotalJobsList = data),
        () => console.error
      );
  }

  private getSelectedMachineTotals({
    machineId: id,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
  }: IMachineJobsFilter): void {
    this.selectedMachineTotals$ = this.dataWorkService.getTotals({
      machineId: id,
      dateRange: { startDate, endDate },
    });
  }

  private getSumOfJobsChartData({
    machineId,
    dateRange,
    graphPeriodType,
    zoneId
  }: IChartFilter): void {
    this.chartService
      .getSumOfJobs({
        machineId,
        dateRange,
        graphPeriodType,
        zoneId
      })
      .pipe(take(1))
      .subscribe(
        (dataFromApi) => {
          this.sumOfJobsChartDataSets = [
            {
              data: dataFromApi.reduce((acc: number[], cur) => {
                acc.push(cur.y);
                return acc;
              }, []),
              label: 'Sum of the Jobs',
              barPercentage: 1,
              categoryPercentage: 1,
            },
          ];

          this.sumOfJobsChartLabels = dataFromApi.reduce(
            (acc: string[], cur) => {
              acc.push(cur.x);
              return acc;
            },
            []
          );
        },
        () => console.error
      );
  }

  private getTotalCutCreaseChartData({
    machineId,
    dateRange,
    graphPeriodType,
    zoneId
  }: IChartFilter): void {
    this.chartService
      .getSumCutCrease({
        machineId,
        dateRange,
        graphPeriodType,
        zoneId
      })
      .pipe(take(1))
      .subscribe(
        (dataFromApi) => {
          this.totalCutCreaseChartDataSets = [
            {
              data: dataFromApi.reduce((acc: number[], cur) => {
                acc.push(cur.y);
                return acc;
              }, []),
              label: 'Total Cut and Crease meters',
              barPercentage: 1,
              categoryPercentage: 1,
            },
          ];

          this.totalCutCreaseChartLabels = dataFromApi.reduce(
            (acc: string[], cur) => {
              acc.push(cur.x);
              return acc;
            },
            []
          );
        },
        () => console.error
      );
  }

  private getMachineRunningRatio({
    machineId,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
    zoneId
  }: IMachineJobsFilter): void {
    this.dataStateService
      .getRunningRatio({ machineId, dateRange: { startDate, endDate }, zoneId })
      .pipe(take(1))
      .subscribe(
        (data) => (this.machineRunningRatio = data),
        () => console.error
      );
  }

  private getMachineErrorRatio({
    machineId,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
    zoneId
  }: IMachineJobsFilter): void {
    this.dataStateService
      .getErrorRatio({ machineId, dateRange: { startDate, endDate }, zoneId })
      .pipe(take(1))
      .subscribe(
        (data) => (this.machineErrorRatio = data),
        () => console.error
      );
  }

  private getMachineWaitRatio({
    machineId,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
    zoneId
  }: IMachineJobsFilter): void {
    this.dataStateService
      .getWaitRatio({ machineId, dateRange: { startDate, endDate }, zoneId })
      .pipe(take(1))
      .subscribe(
        (data) => (this.machineWaitRatio = data),
        () => console.error
      );
  }

  private async getMachinOverallConsumption({
    customerId,
    machineId,
    dateRange: { startDate, endDate } = {
      startDate: moment().startOf('day'),
      endDate: moment().endOf('day'),
    },
    zoneId
  }: IMachineJobsFilter): Promise<void> {
    const featurePowerEnabled = (await this.featureToggleService.getFeatureToggle('FEAT_POWER')).enabled;
    if (featurePowerEnabled) {
      this.dataEnergyService
        .getOverallConsumption({ customerId, machineId, dateRange: { startDate, endDate }, zoneId })
        .pipe(take(1))
        .subscribe(
          (data) => (this.machineOverallConsumption = data !== undefined && data !== null ? data / 1000 : undefined),
          () => console.error
        );
      this.machinePowerAlgorithm = this.selectedMachine?.actualMeasurementAlgorithm;
    }
  }
}
